import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useTeamSites } from 'dashboard/hooks/useTeamSites';
import { useDepartmentsForSites } from 'shared/hooks/api/departments';
import type { SelectionDropdownOption } from 'shared/components/dropdown/SelectionDropdown';

import * as OverviewStyled from 'dashboard/styles/Overview.style';
import * as RechartsStyled from 'dashboard/styles/Recharts.style';
import * as Styled from 'dashboard/styles/Compare.style';
import * as Buttons from 'theme/components/Buttons.style';
import * as Text from 'theme/components/Text.style';
import CompareColumn from './CompareColumn';

interface CompareSectionProps {
  queryParams: string;
  siteIds: string[];
}

function resolveSelectedId(
  options: SelectionDropdownOption[],
  selectedId: string | null,
  fallbackIndex: number
): string | undefined {
  const isAvailable = options.some((option) => option.value === selectedId);
  return isAvailable && selectedId ? selectedId : options[fallbackIndex]?.value;
}

export default function CompareSection({
  queryParams,
  siteIds,
}: CompareSectionProps): JSX.Element | null {
  const { t } = useTranslation('pageDashboard');
  const { data: sites = [], isSuccess: sitesLoaded } = useTeamSites();

  const compareSites = useMemo(
    () =>
      siteIds.length > 0
        ? sites.filter((site) => siteIds.includes(site.id))
        : sites,
    [sites, siteIds]
  );

  const { departmentsBySite, isSuccess: departmentsLoaded } =
    useDepartmentsForSites(compareSites.map((site) => site.id));

  const departmentOptions = useMemo(() => {
    const siteNameById = new Map(
      compareSites.map((site) => [site.id, site.name])
    );
    return departmentsBySite.flatMap(({ siteId, departments }) =>
      departments.map((department) => ({
        value: department.id,
        label: t('compare.departmentOption', {
          site: siteNameById.get(siteId),
          department: department.name,
        }),
      }))
    );
  }, [compareSites, departmentsBySite, t]);

  const [leftDepartmentId, setLeftDepartmentId] = useState<string | null>(null);
  const [rightDepartmentId, setRightDepartmentId] = useState<string | null>(
    null
  );
  const [showDetails, setShowDetails] = useState(false);

  const leftId = resolveSelectedId(departmentOptions, leftDepartmentId, 0);
  const rightId = resolveSelectedId(departmentOptions, rightDepartmentId, 1);

  if (!sitesLoaded || compareSites.length === 0 || !departmentsLoaded) {
    return null;
  }

  return (
    <OverviewStyled.SectionContainer>
      <OverviewStyled.SectionHeader>
        <Text.H5>{t('compare.title')}</Text.H5>
      </OverviewStyled.SectionHeader>

      <OverviewStyled.Card>
        {leftId && rightId ? (
          <>
            <Styled.CompareGrid>
              <CompareColumn
                queryParams={queryParams}
                departmentOptions={departmentOptions}
                selectedDepartmentId={leftId}
                onDepartmentChange={setLeftDepartmentId}
                showDetails={showDetails}
              />
              <CompareColumn
                queryParams={queryParams}
                departmentOptions={departmentOptions}
                selectedDepartmentId={rightId}
                onDepartmentChange={setRightDepartmentId}
                showDetails={showDetails}
              />
            </Styled.CompareGrid>

            <Styled.ShowMoreRow>
              <Buttons.SecondaryLight
                type="button"
                $size="small"
                onClick={() => setShowDetails((previous) => !previous)}
              >
                {showDetails ? t('compare.showLess') : t('compare.showMore')}
              </Buttons.SecondaryLight>
            </Styled.ShowMoreRow>
          </>
        ) : (
          <RechartsStyled.ChartEmptyCentered>
            <RechartsStyled.ChartEmptyCard>
              <RechartsStyled.ChartEmptyTextContent>
                <RechartsStyled.ChartEmptyTitle>
                  {t('compare.notEnoughDepartments')}
                </RechartsStyled.ChartEmptyTitle>
                <RechartsStyled.ChartEmptyDescription>
                  {t('compare.notEnoughDepartmentsDescription')}
                </RechartsStyled.ChartEmptyDescription>
              </RechartsStyled.ChartEmptyTextContent>
            </RechartsStyled.ChartEmptyCard>
          </RechartsStyled.ChartEmptyCentered>
        )}
      </OverviewStyled.Card>
    </OverviewStyled.SectionContainer>
  );
}
